import { authenticatedInstance } from '../index/index.service';
import type { ReceiptView } from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';

export type { ReceiptView } from 'shared-models/browser';

class ReceiptService {
  // Summary: Fetch the assembled receipt; pass paymentlineId for a single split's partial.
  // Details: docs/function-library/web/services.md#receipt-service
  async getReceipt(
    tenantId: string,
    orderId: number,
    employeeSession: string,
    paymentlineId?: number,
  ): Promise<ReceiptView> {
    try {
      const response = await authenticatedInstance.get<ReceiptView>(
        `/receipt/tenant/${encodeURIComponent(tenantId)}/order/${orderId}`,
        {
          params: paymentlineId != null ? { paymentlineId } : undefined,
          headers: { 'x-employee-session': employeeSession },
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed to load receipt'));
    }
  }

  // Summary: Tenant's current VAT rate + resolved header for the pre-payment bill preview.
  async getTenantVatRate(
    tenantId: string,
    employeeSession: string,
    orderId?: number,
  ): Promise<{ vatRatePercent: number; header: string; openedByName?: string | null }> {
    try {
      const response = await authenticatedInstance.get<{ vatRatePercent: number; header: string; openedByName?: string | null }>(
        `/receipt/tenant/${encodeURIComponent(tenantId)}/vat-rate`,
        {
          params: orderId != null ? { orderId } : undefined,
          headers: { 'x-employee-session': employeeSession },
        },
      );
      return {
        vatRatePercent: response.data.vatRatePercent,
        header: response.data.header,
        openedByName: response.data.openedByName ?? null,
      };
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed to load VAT rate'));
    }
  }
}

export const receiptService = new ReceiptService();
